import { createContext, useContext, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Home, Receipt, FileText, Building2, Megaphone, Wrench, User, LogOut, Sun, Moon,
  MessageSquare, FileSignature,
} from 'lucide-react';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { OrgLogo } from '@/components/OrgLogo';
import { Spinner, cn, EmptyState, Button } from '@/components/ui';
import { FeedbackIconButton } from '@/components/Feedback';
import type { TenantPortalSummary } from '@shared/types';

/**
 * The tenant portal.
 *
 * Deliberately a different shape from the manager app: bottom navigation on
 * mobile, because that is where tenants actually use it, and far fewer choices
 * on screen.
 */

interface PortalData extends TenantPortalSummary {
  utilities: { name: string; category: string; provider: string | null; billing_mode: string }[];
}

interface PortalContext {
  data: PortalData;
  reload: () => void;
}

const Ctx = createContext<PortalContext | null>(null);

export function usePortal(): PortalContext {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('usePortal must be used inside the portal shell');
  return ctx;
}

/*
 * The first five are what a phone's bottom bar can show, and they are the five
 * things a tenant either has to do or is chasing: what they owe, what they
 * have been sent, a conversation they started, and an agreement waiting for
 * their name. "My home" is reference — worth having, not worth a thumb's
 * width — so it sits below the fold on a phone.
 */
const NAV = [
  { to: '/portal', label: 'Home', icon: Home, end: true },
  { to: '/portal/payments', label: 'Rent', icon: Receipt },
  { to: '/portal/documents', label: 'Documents', icon: FileText },
  { to: '/portal/messages', label: 'Messages', icon: MessageSquare, badge: 'unread' as const },
  { to: '/portal/agreement', label: 'Agreement', icon: FileSignature },
  { to: '/portal/property', label: 'My home', icon: Building2 },
  { to: '/portal/updates', label: 'Updates', icon: Megaphone },
  { to: '/portal/maintenance', label: 'Repairs', icon: Wrench },
  { to: '/portal/profile', label: 'Profile', icon: User },
];

function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    try {
      // Exact match only, so a junk value lands on the default rather than
      // being set as the attribute. Light is the default; see src/main.tsx,
      // which has to agree with this or the first paint flashes.
      return localStorage.getItem('provia.theme') === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('provia.theme', theme); } catch { /* storage blocked */ }
  }, [theme]);
  return { theme, toggle: () => setTheme((t) => (t === 'dark' ? 'light' : 'dark')) };
}

export default function PortalShell() {
  const { tenant, signOut } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const { data, loading, error, reload } = useApi<PortalData>('/portal/summary');

  /*
   * Only while there is nothing to show.
   *
   * This used to fire on every refetch, which unmounts the whole Outlet — so a
   * screen that refreshed the summary (Messages, to clear its unread badge)
   * was unmounted mid-refresh, remounted, refreshed again, forever. A
   * background reload must leave the page you are on alone.
   */
  if (loading && !data) {
    return <div className="min-h-screen flex items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <EmptyState
          title="Could not load your details"
          message={error ?? 'Please try again in a moment.'}
          action={<Button variant="primary" onClick={reload}>Retry</Button>}
        />
      </div>
    );
  }

  return (
    <Ctx.Provider value={{ data, reload }}>
      <div className="min-h-screen bg-bg pb-20 lg:pb-0">
        <header className="sticky top-0 z-20 bg-surface/90 backdrop-blur border-b border-line">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="flex items-center gap-3 h-16">
              <OrgLogo fileId={data.org.logo_file_id} className="h-7 w-7" />
              <div className="min-w-0">
                <p className="font-extrabold tracking-tight leading-none">{data.org.name}</p>
                <p className="text-[11px] text-faint truncate mt-0.5">{tenant?.full_name}</p>
              </div>

              <div className="ml-auto flex items-center gap-1">
                <button
                  onClick={toggle}
                  className="p-2 rounded-lg text-faint hover:text-ink hover:bg-raised transition-colors"
                  aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
                >
                  {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <FeedbackIconButton />
                <button
                  onClick={() => { signOut(); navigate('/'); }}
                  className="flex shrink-0 items-center gap-2 px-2.5 py-2 rounded-lg
                             text-faint transition-colors
                             hover:text-danger hover:bg-danger/10"
                >
                  <LogOut className="h-4 w-4 shrink-0" />
                  <span className="text-sm font-medium">Sign out</span>
                </button>
              </div>
            </div>

            {/* Desktop tabs */}
            <nav className="hidden lg:flex gap-1 -mb-px">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => cn(
                    'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                    isActive ? 'text-ink border-brand' : 'text-muted border-transparent hover:text-ink',
                  )}
                >
                  <item.icon className="h-4 w-4" />
                  {item.label}
                  {item.badge === 'unread' && data.unread_messages > 0 && (
                    <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand
                                     text-white text-[10px] font-bold grid place-items-center">
                      {data.unread_messages}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
        </header>

        <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
          <Outlet />
        </main>

        {/*
          Who is who, from the tenant's side.

          Their landlord is {data.org.name}; the software is PROVIA, built by
          OPSERION. It matters because a tenant chasing a repair should write
          to their landlord and not to us — and because somebody wondering who
          else can see their phone number is entitled to a straight answer
          without hunting for a privacy policy.
        */}
        <footer className="max-w-5xl mx-auto px-4 sm:px-6 pb-8 pt-2">
          <p className="text-[11px] text-faint leading-relaxed">
            This account is run by {data.org.name}, who you should contact about anything to do
            with your tenancy. The software is PROVIA, built and operated by OPSERION on their
            behalf.
          </p>
        </footer>

        {/* Mobile bottom bar */}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-20 bg-surface/95 backdrop-blur
                        border-t border-line pb-[env(safe-area-inset-bottom)]">
          <div className="grid grid-cols-5">
            {NAV.slice(0, 5).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => cn(
                  'flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors',
                  isActive ? 'text-brand' : 'text-faint',
                )}
              >
                <span className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.badge === 'unread' && data.unread_messages > 0 && (
                    <span className="absolute -top-1 -right-1.5 min-w-[15px] h-[15px] px-1
                                     rounded-full bg-brand text-white text-[9px] font-bold
                                     grid place-items-center">
                      {data.unread_messages}
                    </span>
                  )}
                </span>
                {item.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </Ctx.Provider>
  );
}
